import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Paper,
  Typography,
  Button,
  Menu,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Autocomplete,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PublicIcon from "@mui/icons-material/Public";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import SendIcon from "@mui/icons-material/Send";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PolicyIcon from "@mui/icons-material/Policy";
import PaymentIcon from "@mui/icons-material/Payment";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { RootState } from "../../../store/store";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  IGetInitBasePromptRequestParams,
  IMessage,
} from "../../../models/user/InsureAssist/InsureAssist";
import { getCookie } from "../../../utils/Utility";
import i18n from "../../../translator/i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import ThreeDotsLoader from "../../../components/ThreeDotsLoader/ThreeDotsLoader";
import RightInfoPanel from "./RightPanel";
import { Padding } from "@mui/icons-material";
import VoiceChatToggle from "./VoiceChatToggle";
import ChatModeSwitch from "./ChatModeSwitch";
import {
  startAudioRecording,
  stopAudioRecording,
  cancelAudioRecording,
} from "../../Dashboard/InsureAssist/AudioRecording";
import axios from "axios";
import CustomSkeleton from "../../../common/CustomSkeleton/CustomSkeleton";
import { GetInitBasePrompt } from "../../Dashboard/DashboardAction";
import MentionInput from "../../Dashboard/InsureAssist/MentionInput";
import { brandColors } from "./brandColors";

interface ChatHeaderProps {
  onAdd?: () => void;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({ onAdd }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, calledReducerType } = useAppSelector(
    (state: RootState) => state.common
  );
  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);
  const { initResponse } = useAppSelector(
    (state: RootState) => state.insureAssist
  );
  const { policiesData } = useAppSelector(
    (state: RootState) => state.dashboard
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [input, setInput] = useState<string>("");
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [loading, setLoading] = useState(false);
  const [convoId, setConvoId] = useState<string | null>(null);
  const [pendingQueue, setPendingQueue] = useState<IMessage[]>([]);
  const newMessageRef = useRef<HTMLDivElement | null>(null);
  const pingIntervalRef = useRef<number | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const unmountedRef = useRef<boolean>(false);
  const suppressReconnectRef = useRef<boolean>(false);
  const hasMessages = messages.length > 0;
  const [panelCard, setPanelCard] = useState<any | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [showVoicePanel, setShowVoicePanel] = useState<boolean>(false);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>("default");
  const [audioDevices, setAudioDevices] = useState<Array<{ deviceId: string; label: string }>>([]);
  const [voiceActive, setVoiceActive] = useState<boolean>(false);
  const [voiceSpeaking, setVoiceSpeaking] = useState<boolean>(false);
  const [voiceStartTs, setVoiceStartTs] = useState<number | null>(null);
  const resetConversationRef = useRef<(() => void) | null>(null);
  const stopVoiceRef = useRef<(() => void) | null>(null);

  const wsUrl = process.env.REACT_APP_CHAT_INTENT_WEB_SOCKET_URL;
  const transcriptUrl = process.env.REACT_APP_CHAT_TRANSCRIPT_URL;
  const sessionId = getCookie("sessionId");
  const roleCode = getCookie("roleCode");
  const producerId = getCookie("id");
  const shouldShowRightPanel =
    producerId === "0987" ? Boolean(panelCard) : true;

  // Special-case: stop all backend/chat network calls for this customer.
  // `customerId` used for `GetInitBasePrompt` is the same `producerId` cookie.
  const disableApiForCustomer0987 = producerId === "0987";

  // Recording state
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [unsupportedBrowser, setUnsupportedBrowser] = useState<boolean>(false);
  const [elapsedTime, setElapsedTime] = useState<string>("00:00");
  const [transcribeLoading, setTranscribeLoading] = useState<boolean>(false);
  const [transcribeError, setTranscribeError] = useState<boolean>(false);

  const handleQuickActionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleQuickAction = (action: string) => {
    console.log(`Quick action selected: ${action}`);
    // Add your logic here for each action
    handleClose();
  };

  const handleCopyQuestion = async (question: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering the question click
    try {
      await navigator.clipboard.writeText(question);
      // You could add a toast notification here if needed
      console.log('Question copied to clipboard');
    } catch (err) {
      console.error('Failed to copy question:', err);
    }
  };

  const handleRefresh = () => {
    // Clear messages and reset to initial state
    setMessages([]);
    setConvoId(null);
    setInput("");
    setLoading(false);
    setPanelCard(null);
    setShowVoicePanel(false);
    // Clear session storage
    sessionStorage.removeItem("chatHeaderMessages");
    sessionStorage.removeItem("chatHeaderConvoId");
  };

  const suggestedQuestions = useMemo(() => {
    // For customer 0987, show fixed suggested questions and skip parsing API response
    if (disableApiForCustomer0987) {
      return [
        "Download my tax certificate",
        "Why is my health score low?",
        "How can I improve my score?",
        "What changed in my health this week?",
      ];
    }

    let titles: string[] = [];

    if (initResponse) {
      if (initResponse.includes("-> ")) {
        titles = initResponse.split("-> ").slice(1);
      } else if (initResponse.includes("->")) {
        titles = initResponse.split("->").slice(1);
      }
    }
    return titles;
  }, [initResponse, disableApiForCustomer0987]);

  const upcomingDueAmount = useMemo(() => {
    if (
      policiesData &&
      policiesData.policyList &&
      policiesData.policyList.length > 0
    ) {
      const duePolicy = policiesData.policyList.find((policy: any) => {
        if (!policy?.dueDate || !policy?.dueAmount) return false;
        const daysToDue = moment(policy.dueDate).diff(moment(), "days");
        return daysToDue <= 7;
      });
      return duePolicy?.dueAmount ?? null;
    }
    return null;
  }, [policiesData]);

  useEffect(() => {
    sessionStorage.removeItem("chatHeaderMessages");
    if (!disableApiForCustomer0987 && producerId) {
      const requestParams: IGetInitBasePromptRequestParams = {
        customerId: producerId,
        language: i18n.language,
      };
      dispatch(GetInitBasePrompt(requestParams));
    }
  }, []);

  // Enumerate audio input devices for voice panel
  useEffect(() => {
    let mounted = true;
    const listDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const mics = devices
          .filter((d) => d.kind === "audioinput")
          .map((d, idx) => ({
            deviceId: d.deviceId || "default",
            label: d.label && d.label.trim().length > 0 ? d.label : `Microphone ${idx + 1}`,
          }));
        if (mounted) setAudioDevices(mics);
      } catch {}
    };
    // Attempt to get permission silently for labels where possible
    const primePermissions = async () => {
      try {
        // Use minimal constraints; don't keep the stream
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
      } catch {}
      listDevices();
    };
    // If mediaDevices present, wire device change and list
    if (navigator.mediaDevices) {
      primePermissions();
      const handler = () => listDevices();
      try {
        navigator.mediaDevices.addEventListener("devicechange", handler);
      } catch {}
      return () => {
        mounted = false;
        try {
          navigator.mediaDevices.removeEventListener("devicechange", handler);
        } catch {}
      };
    }
  }, []);
  const generateIAApiUrl = (role: string, producer?: string) => {
    if (role === "2") {
      return `${wsUrl}?customerId=${userDetails?.customerId}&language=${i18n.language}&sessionId=${sessionId}&userType=Customer`;
    } else if (role === "Producer" && producer && producer.trim()) {
      return `${wsUrl}?customerId=${userDetails?.customerId}&language=${i18n.language}&sessionId=${sessionId}&userType=Agent&producerId=${producer}`;
    }
    return undefined;
  };

  const startWebSocket = () => {
    if (!wsUrl || !roleCode) return;
    const url = generateIAApiUrl(roleCode, producerId);
    if (!url) return;
    const ws = new WebSocket(url);

    ws.onopen = () => {
      if (pingIntervalRef.current) {
        window.clearInterval(pingIntervalRef.current);
      }
      pingIntervalRef.current = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          const ping: IMessage = {
            content: "ping",
            creationTime: new Date().toString(),
            level: 0,
            sender: "customer",
            type: "ping",
            convoId: "",
          };
          ws.send(JSON.stringify(ping));
        }
      }, 55000);

      setPendingQueue((queued) => {
        queued.forEach((msg) => ws.send(JSON.stringify(msg)));
        return [];
      });
    };

    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      if (message.type === "SESSION_ESTABLISHED") {
        setConvoId(message.convoId);
        sessionStorage.setItem("chatHeaderConvoId", message.convoId || "");
      } else if (message.type === "pong") {
      } else {
        setConvoId(message.convoId);
        sessionStorage.setItem("chatHeaderConvoId", message.convoId || "");
        // Detect structured card payload in content or at root
        let processed = message as any;
        try {
          if (processed.card) {
            setPanelCard(processed.card);
            if (processed.narrative) {
              processed = {
                ...processed,
                type: "text",
                content: processed.narrative,
              };
            }
          } else if (typeof processed.content === "string") {
            const maybe = JSON.parse(processed.content);
            if (maybe && (maybe.card || maybe.narrative)) {
              if (maybe.card) setPanelCard(maybe.card);
              if (maybe.narrative) {
                processed = {
                  ...processed,
                  type: "text",
                  content: maybe.narrative,
                };
              }
            }
          }
        } catch {}
        setMessages((prev) => {
          const updated = [...prev, processed];
          sessionStorage.setItem(
            "chatHeaderMessages",
            JSON.stringify(updated)
          );
          return updated;
        });
        setLoading(false);
      }
    };

    ws.onclose = () => {
      setSocket(null);
      if (pingIntervalRef.current) {
        window.clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (!unmountedRef.current && !suppressReconnectRef.current) {
        if (reconnectTimeoutRef.current)
          window.clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = window.setTimeout(() => {
          startWebSocket();
        }, 800);
      }
    };

    ws.onerror = () => {
      setSocket(null);
      if (pingIntervalRef.current) {
        window.clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      try {
        ws.close();
      } catch {}
    };

    setSocket(ws);
  };

  useEffect(() => {
    const storedMessages = sessionStorage.getItem("chatHeaderMessages");
    const storedConvoId = sessionStorage.getItem("chatHeaderConvoId");
    if (storedMessages) setMessages(JSON.parse(storedMessages));
    if (storedConvoId) setConvoId(storedConvoId);
    startWebSocket();
    return () => {
      unmountedRef.current = true;
      if (pingIntervalRef.current)
        window.clearInterval(pingIntervalRef.current);
      if (reconnectTimeoutRef.current)
        window.clearTimeout(reconnectTimeoutRef.current);
      if (socket && socket.readyState === WebSocket.OPEN) socket.close();
      try {
        cancelAudioRecording();
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsUrl, roleCode, producerId, userDetails?.customerId]);

  const audioDeviceOptions = useMemo(() => {
    const uniqueDevices = audioDevices.filter(
      (device, index, arr) =>
        device.deviceId !== "default" &&
        arr.findIndex((d) => d.deviceId === device.deviceId) === index
    );
    return [{ deviceId: "default", label: "System Default" }, ...uniqueDevices];
  }, [audioDevices]);

  const selectedAudioOption = useMemo(() => {
    return (
      audioDeviceOptions.find((device) => device.deviceId === selectedAudioDevice) ||
      audioDeviceOptions[0] ||
      null
    );
  }, [audioDeviceOptions, selectedAudioDevice]);

  const handleSend = (text?: string) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed) return;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      if (!socket) startWebSocket();
    }
    const messageObj: IMessage = {
      content: trimmed,
      creationTime: new Date().toString(),
      level:
        messages.length !== 0 ? messages[messages.length - 1].level + 1 : 0,
      sender: "customer",
      type: "text",
      convoId: convoId || "",
    };
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(messageObj));
    } else {
      setPendingQueue((q) => [...q, messageObj]);
    }
    setMessages((prev) => {
      const updated = [...prev, messageObj];
      sessionStorage.setItem("chatHeaderMessages", JSON.stringify(updated));
      if (convoId) sessionStorage.setItem("chatHeaderConvoId", convoId);
      return updated;
    });
    setInput("");
    setLoading(true);
  };

  useEffect(() => {
    if (newMessageRef.current) {
      newMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [messages]);

  // Mic handlers (text chat audio recording flow)
  const handleStartRecording = () => {
    if (isRecording) {
      handleStopRecording();
      return;
    }
    try {
      startAudioRecording(
        (value) => setIsRecording(value),
        (value) => setUnsupportedBrowser(value),
        (value) => setElapsedTime(value)
      );
    } catch {}
  };

  // Enter voice chat panel (realtime voice agent UI)
  const handleEnterVoicePanel = () => {
    // If legacy mic recording is active, cancel it (do not finalize/send)
    if (isRecording) {
      try {
        cancelAudioRecording();
      } catch {}
      setIsRecording(false);
    }
    suppressReconnectRef.current = true;
    try {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.close();
      }
    } catch {}
    setSocket(null);
    if (pingIntervalRef.current) {
      window.clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    setMessages([]);
    setConvoId(null);
    setInput("");
    setLoading(false);
    setPanelCard(null);
    sessionStorage.removeItem("chatHeaderMessages");
    sessionStorage.removeItem("chatHeaderConvoId");
    setPendingQueue([]);
    setShowVoicePanel(true);
    setVoiceStartTs(Date.now());
  };

  const transcribeAudio = async (audioMessage: IMessage) => {
    if (transcriptUrl) {
      try {
        setTranscribeLoading(true);
        setTranscribeError(false);
        const response = await axios.post(transcriptUrl, audioMessage, {
          withCredentials: true,
        });
        if (response.status !== 200) {
          throw new Error("Failed to transcribe audio");
        }
        const data = response.data;
        setTranscribeLoading(false);
        return data;
      } catch (error) {
        setTranscribeLoading(false);
        setTranscribeError(true);
        return null;
      }
    }
  };

  const handleStopRecording = () => {
    stopAudioRecording(async (blob) => {
      if (blob) {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = async () => {
          const base64data = reader.result;
          if (typeof base64data === "string") {
            const audioMessage: IMessage = {
              content: base64data,
              creationTime: new Date().toString(),
              level: messages.length,
              sender: "customer",
              type: "audio",
              convoId: convoId || "",
            };
            // send or queue
            if (socket && socket.readyState === WebSocket.OPEN) {
              socket.send(JSON.stringify(audioMessage));
            } else {
              setPendingQueue((q) => [...q, audioMessage]);
            }
            // add to local messages
            setMessages((prev) => {
              const updated = [...prev, audioMessage];
              sessionStorage.setItem(
                "chatHeaderMessages",
                JSON.stringify(updated)
              );
              if (convoId)
                sessionStorage.setItem("chatHeaderConvoId", convoId);
              return updated;
            });
            setLoading(true);
            // optional transcription
            const transcript = await transcribeAudio(audioMessage);
            if (transcript) {
              setMessages((prev) => {
                const updated = [...prev];
                // Find the same audio message by a stable key (creationTime content match)
                let idx = -1;
                for (let i = updated.length - 1; i >= 0; i--) {
                  const msg = updated[i] as any;
                  if (
                    msg &&
                    msg.type === "audio" &&
                    msg.sender === "customer" &&
                    msg.creationTime === audioMessage.creationTime &&
                    msg.content === audioMessage.content
                  ) {
                    idx = i;
                    break;
                  }
                }
                if (idx >= 0 && updated[idx]) {
                  (updated[idx] as any).transcription = transcript;
                  try {
                    sessionStorage.setItem(
                      "chatHeaderMessages",
                      JSON.stringify(updated)
                    );
                  } catch {}
                }
                return updated;
              });
            }
          }
        };
      }
      setIsRecording(false);
    });
    try {
      cancelAudioRecording();
    } catch {}
  };

  const handleSwitchToTextChat = () => {
    suppressReconnectRef.current = false;
    try {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.close();
      }
    } catch {}
    setSocket(null);
    if (pingIntervalRef.current) {
      window.clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    setShowVoicePanel(false);
    setMessages([]);
    setConvoId(null);
    setInput("");
    setLoading(false);
    setPanelCard(null);
    sessionStorage.removeItem("chatHeaderMessages");
    sessionStorage.removeItem("chatHeaderConvoId");
    setPendingQueue([]);
    startWebSocket();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 1, md: 1 },
        p: { xs: 0.5, md: 1 },
        width: "100%",
        height: "100%",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: shouldShowRightPanel ? 1 : "1 1 100%",
          overflow: {xs: "auto" ,md : hasMessages && !showVoicePanel ? "hidden" : "auto"},
          borderRadius: { xs: 1, md: 2 },
          background:
            "linear-gradient(180deg, rgba(46,184,172,0.06) 0%, rgba(103,197,228,0.04) 16%, #ffffff 42%)",
          p: { xs: 1, md: 2 },
          gap: {
            xs: "1rem",
            md: hasMessages ? 0 :"3rem",
            xl: hasMessages ? 0 : "3rem",
          },
          minHeight: { xs: "38vh", md: "auto" },
          // Hide scrollbar by default
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          // Show scrollbar on hover (both horizontal and vertical)
          "&:hover": {
            scrollbarWidth: "auto",
            "&::-webkit-scrollbar": {
              display: "block",
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "4px",
              "&:hover": {
                background: "#a8a8a8",
              },
            },
          },
        }}
      >
        {!hasMessages && !showVoicePanel && (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: { xs: 1, md: 1.5 },
                mb: { xs: 0.5, lg: 1, xl: 1.5 },
              }}
            >
              
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  textAlign: "center",
                  fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
                }}
              >
                Welcome {userDetails?.customerName}
              </Typography>
              <Typography
                sx={{
                  textAlign: "center",
                  color: "#6b7280",
                  fontSize: { xs: 13, md: 15 },
                  maxWidth: 420,
                }}
              >
                Your AI insurance assistant is ready to help with policies,
                claims, and payments.
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: { xs: 1, md: 1 },
                mt: { xs: 1, md: 2 },
              }}
            >
              <ChatModeSwitch
                showVoicePanel={showVoicePanel}
                onClickText={() => {
                  if (showVoicePanel) {
                    handleSwitchToTextChat();
                  } else {
                    try {
                      inputRef.current?.focus();
                    } catch {}
                  }
                }}
                onClickVoice={() => {
                  handleEnterVoicePanel();
                }}
              />
            </Box>
          </>
        )}
        {showVoicePanel ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1.5, md: 5 },
              width: "100%",
            }}
          >
            {/* Segmented control visible in voice panel regardless of messages */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: { xs: 2, lg: 3, xl: 5 },
                textAlign: "center",
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
              }}
            >
              Welcome {userDetails?.customerName}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: { xs: 0.5, md: 1 },
              }}
            >
              <ChatModeSwitch
                showVoicePanel={showVoicePanel}
                onClickText={() => {
                  handleSwitchToTextChat();
                  try {
                    inputRef.current?.focus();
                  } catch {}
                }}
                onClickVoice={() => {}}
              />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: { xs: 16, md: 18 } }}>Audio Agent</Typography>
                <Typography sx={{ color: "#6b7280", fontSize: { xs: 12, md: 13 } }}>
                  This chat agent specializes in performing actions and resolving issues effectively.
                </Typography>
              </Box>
              <Autocomplete
                size="small"
                sx={{ minWidth: { xs: 160, md: 240 } }}
                options={audioDeviceOptions}
                disableClearable
                getOptionLabel={(option) => option.label}
                value={selectedAudioOption}
                onChange={(_, value) => {
                  if (value) {
                    setSelectedAudioDevice(value.deviceId);
                  }
                }}
                isOptionEqualToValue={(option, value) =>
                  option.deviceId === value.deviceId
                }
                ListboxProps={{
                  style: {
                    maxHeight: 220,
                    overflowY: "auto",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select audio device"
                    inputProps={{
                      ...params.inputProps,
                      readOnly: true,
                    }}
                  />
                )}
              />
            </Box>
            {/* {voiceStartTs && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1 }}>
                <Typography sx={{ fontSize: 12, color: "#111827", fontWeight: 600 }}>
                  {new Date(Date.now() - voiceStartTs).toISOString().substring(14, 19)}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#10b981", fontWeight: 600 }}>
                  {voiceSpeaking ? "Listening..." : voiceActive ? "Responding..." : "Ready"}
                </Typography>
              </Box>
            )} */}
            <Box
              sx={{
                border: "1px solid #e5e7eb",
                bgcolor: "#ffffff",
                borderRadius: 2,
                p: { xs: 2, md: 2 },
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: { xs: 14, md: 16 } }}>
                  Start a conversation
                </Typography>
                <Typography sx={{ color: "#6b7280", fontSize: { xs: 12, md: 13 } }}>
                  Interact with your AI agent
                </Typography>
              </Box>
              <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
                {voiceActive && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 9999,
                      bgcolor: voiceSpeaking ? "#fef3c7" : "#dcfce7",
                      color: voiceSpeaking ? "#92400e" : "#065f46",
                      border: "1px solid",
                      borderColor: voiceSpeaking ? "#f59e0b" : "#10b981",
                      fontWeight: 700,
                      fontSize: { xs: 12, md: 13 },
                      userSelect: "none",
                    }}
                  >
                    <GraphicEqIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                    {voiceSpeaking ? "Listening..." : "Responding..."}
                  </Box>
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <VoiceChatToggle
                  getConvoId={() => convoId}
                  getMessages={() => messages}
                  setMessages={setMessages}
                  customerId={userDetails?.customerId}
                  iconColor={brandColors.primary}
                  selectedDeviceId={selectedAudioDevice}
                  voiceStartTs={voiceStartTs}
                  onStatusChange={(s) => {
                    setVoiceActive(s.active);
                    setVoiceSpeaking(s.speaking);
                  }}
                  onCard={(card) => {
                    setPanelCard(card);
                  }}
                  onReset={() => {
                    setConvoId(null);
                    setVoiceActive(false);
                    setVoiceSpeaking(false);
                    setVoiceStartTs(null);
                  }}
                  resetConversationRef={resetConversationRef}
                  stopVoiceRef={stopVoiceRef}
                />
                {messages.length > 0 && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      // Reset voice conversation including timer
                      if (resetConversationRef.current) {
                        resetConversationRef.current();
                      }
                      // Also reset ChatHeader state
                      setConvoId(null);
                      setVoiceActive(false);
                      setVoiceSpeaking(false);
                      setVoiceStartTs(null);
                    }}
                    sx={{
                      color: "#6b7280",
                      "&:hover": {
                        bgcolor: "#f3f4f6",
                        color: "#374151",
                      },
                      p: { xs: 0.5, md: 1 },
                    }}
                    title="Reset conversation"
                  >
                    <RefreshIcon sx={{ fontSize: { xs: 20, md: 22 } }} />
                  </IconButton>
                )}
              </Box>
            </Box>
            {/* Voice transcript-style bubbles (show all with scroll) */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                maxHeight: { xs: "32vh", },
                overflowY: "auto",
                pr: { xs: 0.5, md: 1 },
                // Hide scrollbar by default
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                // Show scrollbar on hover
                "&:hover": {
                  scrollbarWidth: "auto",
                  "&::-webkit-scrollbar": {
                    display: "block",
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#c1c1c1",
                    borderRadius: "4px",
                    "&:hover": {
                      background: "#a8a8a8",
                    },
                  },
                },
              }}
            >
              {messages.map((m, i) => (
                <Box
                  key={`${m.creationTime}_${i}`}
                  ref={i === messages.length - 1 ? newMessageRef : null}
                  sx={{ bgcolor: "#e5effe", borderRadius: 2, p: 1.5 }}
                >
                  <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
                    {moment(m.creationTime).format("HH:mm:ss")}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#1f2937", mt: 0.5 }}>{m.content as any}</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75 }}>
                    {/* <Box sx={{ width: 8, height: 8, bgcolor: "#10b981", borderRadius: 9999 }} /> */}
                    {/* <Typography sx={{ fontSize: 12, color: "#10b981", fontWeight: 600 }}>Pass</Typography> */}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <>
            {(!showVoicePanel && (hasMessages)) && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: { xs: 0.5, md: 1 },
                }}
              >
                <ChatModeSwitch
                  showVoicePanel={showVoicePanel}
                  onClickText={() => {
                    if (showVoicePanel) {
                      handleSwitchToTextChat();
                    } else {
                      try {
                        inputRef.current?.focus();
                  } catch {}
                    }
                  }}
                  onClickVoice={() => {
                    handleEnterVoicePanel();
                  }}
                />
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: { xs: 1, md: 2 },
                width: "100%",
              }}
            >
              <Box
                // elevation={0}
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", md: "72vw", lg: "65vw" },
                  borderRadius: { xs: 2, md: 3 },
                  px: { xs: 2, md: 3 },
                  py: hasMessages ? { xs: 1.5, md: 2 } : 0,
                  mt: { xs: 1, md: 2 },
                  border: "1px solid #e5e7eb",
                  bgcolor: "#ffffff",
                  boxShadow: "0 6px 18px rgba(17,24,39,0.06)",
                  display: "flex",
                  flexDirection: "column",
                  // gap: { xs: 2, md: 4 },
                  maxHeight: { xs: "75vh", md: "59vh" },
                  transition: "box-shadow .2s ease, border-color .2s ease",
                  "&:focus-within": {
                    borderColor: brandColors.primary,
                    boxShadow: `0 8px 22px ${brandColors.highlightSoft}, 0 0 0 3px rgba(46,184,172,0.15)`,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    p: { xs: 0.5, md: 1 },
                    flexDirection: "column",
                    gap: { xs: 1.5, md: 2 },
                    overflowY: "auto",
                    // overflowX: "hidden",
                    pr: { xs: 0.5, md: 1 },
                    maxHeight: { xs: "50vh", md: "60vh" },
                    // Hide scrollbar by default
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                    // Show scrollbar on hover
                    "&:hover": {
                      scrollbarWidth: "auto",
                      "&::-webkit-scrollbar": {
                        display: "block",
                        width: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "#f1f1f1",
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#c1c1c1",
                        borderRadius: "4px",
                        "&:hover": {
                          background: "#a8a8a8",
                        },
                      },
                    },
                  }}
                >
                  {messages
                    .filter((m) => (m as any).channel !== "voice")
                    .map((m, idx) => (
                      <Box
                        key={`${m.creationTime}_${idx}`}
                        ref={idx === messages.length - 1 ? newMessageRef : null}
                        sx={{
                          display: "flex",
                          justifyContent:
                            m.sender === "customer" ? "flex-end" : "flex-start",
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: { xs: "85%", sm: "80%", md: "75%" },
                            px: { xs: 1, md: m.sender === "customer" ? 1.5 : 2.5 },
                            py: { xs: 0.75, md: 1 },
                            borderRadius: { xs: 1.5, md: 2 },
                            bgcolor:
                              m.type === "audio"
                                ? "#ffffff"
                                : m.sender === "customer"
                                  ? "rgba(46, 184, 172, 0.12)"
                                  : "#f3f4f6",
                            color: "#111827",
                            border:
                              m.sender === "customer"
                                ? "1px solid rgba(46, 184, 172, 0.25)"
                                : "1px solid transparent",
                            fontSize: { xs: 13, md: 14 },
                            wordBreak: "break-word",
                            minWidth: m.type === "audio" ? "40%" : undefined,
                            position: "relative",
                            "&:hover": m.sender === "customer" ? {
                              "& .copy-icon": {
                                opacity: 1,
                                visibility: "visible"
                              }
                            } : {},
                          }}
                        >
                          {m.type === "audio" ? (
                            <>
                              <audio
                                controls
                                src={m.content as string}
                                style={{ width: "100%" }}
                              ></audio>
                              {(transcribeLoading &&
                                (m.level === messages.length - 1 ||
                                  m.level === messages.length) && (
                                  <Box sx={{ width: "80%" }}>
                                    <Skeleton animation="wave" />
                                  </Box>
                                )) || <></>}
                              {transcribeError ? (
                                <Typography sx={{ color: "red" }}>
                                  Error transcribing audio
                                </Typography>
                              ) : (
                                <></>
                              )}
                              {(m as any).transcription && (
                                <Typography
                                  className="messageStyles"
                                  sx={{
                                    fontFamily:
                                      'ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI Variable Display", "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"',
                                  }}
                                >
                                  {(m as any).transcription}
                                </Typography>
                              )}
                              {m.sender === "customer" && (
                                <IconButton
                                  size="small"
                                  className="copy-icon"
                                  onClick={(e) => handleCopyQuestion(m.content as string, e)}
                                  sx={{
                                    position: "absolute",
                                    bottom: -8,
                                    right: -8,
                                    opacity: 0,
                                    visibility: "hidden",
                                    transition: "opacity 0.2s ease, visibility 0.2s ease",
                                    p: 0.5,
                                    minWidth: "auto",
                                    width: "24px",
                                    height: "24px",
                                    color: "#6b7280",
                                    bgcolor: "#ffffff",
                                    border: "1px solid #e5e7eb",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                    "&:hover": {
                                      bgcolor: "#f9fafb",
                                      color: "#374151",
                                    },
                                  }}
                                  title="Copy"
                                >
                                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              )}
                            </>
                          ) : (
                            <>
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                rehypePlugins={[rehypeRaw]}
                                components={{
                                  table: ({ node, ...props }) => (
                                    <table
                                      style={{
                                        borderCollapse: "collapse",
                                        width: "100%",
                                        overflow: "scroll",
                                      }}
                                      {...props}
                                    />
                                  ),
                                  th: ({ node, ...props }) => (
                                    <th
                                      style={{
                                        border: "1px solid #ddd",
                                        padding: "8px",
                                        backgroundColor: "#f2f2f2",
                                        textAlign: "left",
                                      }}
                                      {...props}
                                    />
                                  ),
                                  td: ({ node, ...props }) => (
                                    <td
                                      style={{
                                        minWidth: "150px",
                                        border: "1px solid #ddd",
                                        padding: "8px",
                                      }}
                                      {...props}
                                    />
                                  ),
                                }}
                                className="messageStyles"
                              >
                                {m.content}
                              </ReactMarkdown>
                              {m.sender === "customer" && (
                                <IconButton
                                  size="small"
                                  className="copy-icon"
                                  onClick={(e) => handleCopyQuestion(m.content as string, e)}
                                  sx={{
                                    position: "absolute",
                                    bottom: -8,
                                    right: -8,
                                    opacity: 0,
                                    visibility: "hidden",
                                    transition: "opacity 0.2s ease, visibility 0.2s ease",
                                    p: 0.5,
                                    minWidth: "auto",
                                    width: "24px",
                                    height: "24px",
                                    color: "#6b7280",
                                    bgcolor: "#ffffff",
                                    border: "1px solid #e5e7eb",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                    "&:hover": {
                                      bgcolor: "#f9fafb",
                                      color: "#374151",
                                    },
                                  }}
                                  title="Copy"
                                >
                                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>
                    ))}

                  {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: `ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI Variable Display", "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"`,
                        }}
                      >
                        Fetching details
                      </Typography>
                      <ThreeDotsLoader />
                    </Box>
                  ) : (
                    <></>
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    px: { xs: 0.5, md: 0 },
                  }}
                >
                  <MentionInput
                    value={input}
                    onChange={setInput}
                placeholder={
                  isRecording
                    ? `${elapsedTime}`
                    : upcomingDueAmount ? !hasMessages
                    ? `You've got an upcoming auto bill due`: "Type..."
                    : `Ask anything about Policies, Claims...`
                }
                    disabled={isRecording}
                    inputRef={inputRef}
                    variant="chatHeader"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    px: { xs: 0.5, md: 0 },
                    gap: { xs: 1, md: 0 },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {/* <IconButton
              size="small"
              onClick={onAdd}
              sx={{ color: "#6b7280", "&:hover": { bgcolor: "#f3f4f6" } }}
            >
              <AddIcon sx={{ fontSize: 18 }} />
            </IconButton> */}
                    {/* <Box
              onClick={handleQuickActionsClick}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 9999,
                cursor: "pointer",
                color: "#374151",
                "&:hover": { bgcolor: "#f3f4f6" },
              }}
            >
              <PublicIcon sx={{ fontSize: 18, color: "#6b7280" }} />
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                Quick Actions
              </Typography>
              <ExpandMoreIcon sx={{ fontSize: 18, color: "#6b7280" }} />
            </Box> */}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 0.25, md: 0.5 },
                    }}
                  >
                    <IconButton
                      size="small"
                      sx={{
                        color: "#6b7280",
                        "&:hover": { bgcolor: "#f3f4f6" },
                        p: { xs: 0.5, md: 1 },
                      }}
                      onClick={handleStartRecording}
                      title={isRecording ? "Click to Stop" : "Click to Speak"}
                    >
                      <KeyboardVoiceIcon
                        sx={{
                          fontSize: { xs: 16, md: 18 },
                          color: isRecording ? "#ef4444" : undefined,
                        }}
                      />
                    </IconButton>
                    {/* <VoiceChatToggle
                  getConvoId={() => convoId}
                  getMessages={() => messages}
                  setMessages={setMessages}
                  voiceStartTs={voiceStartTs}
                /> */}
                    <IconButton
                      size="small"
                      sx={{
                        color: input.trim() ? "#ffffff" : "#6b7280",
                        bgcolor: input.trim() ? brandColors.primary : "transparent",
                        transition: "all .15s ease",
                        "&:hover": {
                          bgcolor: input.trim()
                            ? brandColors.primary
                            : "#f3f4f6",
                          filter: input.trim() ? "brightness(0.95)" : "none",
                        },
                        "&.Mui-disabled": {
                          bgcolor: "transparent",
                          color: "#d1d5db",
                        },
                        p: { xs: 0.5, md: 1 },
                      }}
                      onClick={() => handleSend()}
                      disabled={isRecording || loading || !input.trim()}
                    >
                      <SendIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                    </IconButton>
                    {hasMessages && (
                      <IconButton
                        size="small"
                        onClick={handleSwitchToTextChat}
                        sx={{
                          color: "#6b7280",
                          "&:hover": {
                            bgcolor: "#f3f4f6",
                            color: "#374151",
                          },
                          p: { xs: 0.5, md: 1 },
                        }}
                        title="Start fresh conversation"
                      >
                        <RefreshIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Box>
              {suggestedQuestions &&
                suggestedQuestions.length > 0 &&
                !hasMessages &&
                !showVoicePanel && (
                  <Box
                    sx={{
                      width: "100%",
                      mb: { xs: 1, md: 2 },
                      mt: { xs: 2, md: 4 },
                      px: { xs: 1, md: 0 },
                    }}
                  >
                    <Typography
                      sx={{
                        textAlign: "center",
                        fontWeight: 700,
                        fontSize: { xs: 13, md: 14 },
                        color: "#374151",
                        mb: { xs: 1, md: 1.5 },
                      }}
                    >
                      What can I help you with today?
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: { xs: 0.75, md: 1 },
                        justifyContent: {
                          xs: "start",
                          md: "center",
                        },
                        px: { xs: 1, md: 2 },
                      }}
                    >
                      {isLoading &&
                        calledReducerType === "dashboard/getInitBasePrompt" ? (
                        <Box>
                          <CustomSkeleton
                            count={5}
                            direction="column"
                            height="40px"
                            width="350px"
                            variant="rounded"
                          />
                        </Box>
                      ) : (
                        suggestedQuestions.map((title, index) => (
                          <Box
                            key={`${title}_${index}`}
                            sx={{
                            py: { xs: 1, md: 1.5 },
                            px: { xs: 1, sm: 1.5, xl: 2 },
                            borderRadius: 9999,
                            border: "1px solid #e5e7eb",
                            bgcolor: "#f9fafb",
                            fontSize: { xs: 10, sm: 11, xl: 12 },
                            color: "#374151",
                            cursor: "pointer",
                            transition: "all .15s ease",
                            "&:hover": {
                              //bgcolor: brandColors.highlightSoft,
                              borderColor: brandColors.primary,
                              color: "#111827",
                              transform: "translateY(-1px)",
                            },
                              maxWidth: {
                                xs: "100%",
                                sm: "auto",
                                md: "550px",
                              },
                              whiteSpace: { xs: "normal", sm: "nowrap" },
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              minWidth: { xs: "auto", sm: "120px" },
                              textAlign: { xs: "center", sm: "left" },
                            }}
                            title={title}
                            onClick={() => handleSend(title)}
                          >
                            {title}
                          </Box>
                        ))
                      )}
                    </Box>
                  </Box>
                )}
            </Box>
          </>
        )}
      </Paper>
      {shouldShowRightPanel && (
      <RightInfoPanel
        card={panelCard}
        onAction={(a) => {
          // If voice is active, stop it and switch UI to text mode
          try { stopVoiceRef.current && stopVoiceRef.current(); } catch {}
          setShowVoicePanel(false);
          setVoiceActive(false);
          setVoiceSpeaking(false);
          // Check if action has a card in params (for edit_driver, etc.)
          const actionParams = (a as any)?.params;
          if (actionParams?.card) {
            setPanelCard(actionParams.card);
            return;
          }
          const text = (a as any)?.followup_message || a?.label || "";
          if (text) {
            handleSend(text);
          }
        }}
      />
      )}
    </Box>
  );
};

export default ChatHeader;
